import { useTranslation } from 'react-i18next';
import { useState, useEffect, use } from 'react';
import {
  contentTypesMapping,
  contentTypesIconsMapping,
  mapAllowedTypesByPlatform,
  availableContentTypes,
  canCollectComments,
} from '../../../utils/contentTypes';
import { ToggleButton } from '../../ToggleButton/ToggleButton';
import { ToggleButtonGroup } from '../../ToggleButtonGroup/ToggleButtonGroup';
import { FilterCounter } from '../../FilterCounter/FilterCounter';
import { useCurrentUser } from '../../../contexts/CurrentUser';

type ContentTypesFilterProps = {
  selected: string[];
  onChange: (selected: string[], collectComments: boolean) => void;
  error?: string;
  isDisabled?: boolean;
  collectComments?: boolean;
  selectedPlatforms?: string[];
};

export const ContentTypesFilter: React.FC<ContentTypesFilterProps> = ({
  selected,
  selectedPlatforms,
  onChange,
  error,
  isDisabled,
  collectComments,
}) => {
  const { t } = useTranslation();

  const [currentUser] = useCurrentUser();

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const collectCommentsToPostsFlag = featureFlags?.includes(
    'collect_comments_to_posts',
  );

  const collectCommentsToPosts = collectComments || false;

  let allowedTypes = new Set<string>(availableContentTypes);

  if (selectedPlatforms && selectedPlatforms?.length > 0) {
    allowedTypes = new Set<string>(
      availableContentTypes.filter((type) => {
        return selectedPlatforms?.some((platform) => {
          return mapAllowedTypesByPlatform[
            platform as keyof typeof mapAllowedTypesByPlatform
          ]?.includes(type);
        });
      }),
    );
  }

  useEffect(() => {
    const filteredSelected = selected?.filter((type) => allowedTypes.has(type));
    const collect =
      canCollectComments(filteredSelected, selectedPlatforms) &&
      collectCommentsToPosts;
    onChange(filteredSelected, collect);
  }, [selectedPlatforms]);

  return (
    <div className={error ? 'form-element error' : 'form-element'}>
      <label>
        {t('Content types')}
        <FilterCounter
          selectedCount={selected.length}
          totalCount={availableContentTypes.length}
          onReset={() => {
            onChange([], false);
            // setCollectCommentsToPosts(false);
          }}
          isDisabled={isDisabled}
        />
      </label>
      <ToggleButtonGroup
        padding="0"
        border="none"
        backgroundColor="transparent"
      >
        {availableContentTypes.map((contentType) => (
          <ToggleButton
            key={contentType}
            text={t(contentTypesMapping[contentType])}
            icon={contentTypesIconsMapping[contentType]}
            selected={selected?.includes(contentType)}
            disabled={isDisabled || !allowedTypes.has(contentType)}
            width={'135px'}
            onChange={() => {
              const newSelected = selected?.includes(contentType)
                ? selected?.filter((ct) => ct !== contentType)
                : [...selected, contentType];
              const collect =
                collectCommentsToPosts &&
                canCollectComments(newSelected, selectedPlatforms);
              onChange(newSelected, collect);
            }}
          />
        ))}
      </ToggleButtonGroup>

      {collectCommentsToPostsFlag ? <div className="form-element">
        <div className="form-checkbox-group">
          <span
            className={
              'form-checkbox' +
              (!canCollectComments(selected, selectedPlatforms) || isDisabled
                ? ' disabled'
                : '')
            }
          >
            <input
              type="checkbox"
              checked={
                collectCommentsToPosts &&
                canCollectComments(selected, selectedPlatforms)
              }
              id="collectCommentsToPosts"
              disabled={!canCollectComments(selected, selectedPlatforms) || isDisabled}
              onChange={(e) => {
                onChange(selected, e.target.checked);
              }}
            />
            <label className="label" htmlFor="collectCommentsToPosts">
              {t('Collect comments')}
            </label>
          </span>
        </div>
      </div> : ''}
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};
